"use client";

import { Trash2 } from "lucide-react";
import Link from "next/link";

import { ProductImage } from "@/components/catalog/product-image";
import { QuantityStepper } from "@/components/catalog/quantity-stepper";
import type { SessionCartItem } from "@/domain/cart/cart-item";
import { formatCatalogMoney } from "@/lib/catalog/money";
import { calculateCartLinePrice } from "@/services/pricing/pricing-service";

export interface CartLineItemProps {
  readonly item: SessionCartItem;
  readonly isWholesaleOrder: boolean;
  readonly onQuantityChange: (quantity: number) => void;
  readonly onRemove: () => void;
}

export function CartLineItem({
  item,
  isWholesaleOrder,
  onQuantityChange,
  onRemove,
}: Readonly<CartLineItemProps>) {
  const linePrice = calculateCartLinePrice(item, isWholesaleOrder);

  return (
    <article className="rounded-xl border border-outline-variant bg-surface p-3 shadow-sm">
      <div className="grid grid-cols-[72px_1fr_auto] gap-3">
        <Link
          className="flex h-20 items-center justify-center overflow-hidden rounded-lg bg-white p-[5px]"
          href={`/productos/${item.slug}`}
          prefetch={false}
        >
          <ProductImage alt={item.name} imageUrl={item.imageUrl} />
        </Link>
        <div className="min-w-0">
          <Link
            className="line-clamp-2 text-sm font-extrabold leading-5 text-foreground"
            href={`/productos/${item.slug}`}
            prefetch={false}
          >
            {item.name}
          </Link>
          <p className="mt-1 text-xs text-muted">
            {item.type === "generico" ? "Genérico" : "Original"}
            {item.brandName ? ` - ${item.brandName}` : ""}
          </p>
          <p className="mt-1 text-sm font-extrabold text-secondary">
            {formatCatalogMoney(linePrice.unitPrice, item.currencyCode)}
          </p>
          {linePrice.priceKind === "wholesale" ? (
            <p className="text-xs font-semibold text-secondary">Precio mayorista aplicado</p>
          ) : null}
          {item.wholesalePrice && item.wholesaleMinQuantity ? (
            <p className="text-xs font-semibold text-secondary">
              Mayor desde {item.wholesaleMinQuantity} uds.
            </p>
          ) : null}
        </div>
        <button
          aria-label={`Eliminar ${item.name}`}
          className="inline-flex h-11 w-11 items-center justify-center text-muted"
          onClick={onRemove}
          type="button"
        >
          <Trash2 aria-hidden="true" size={18} />
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <QuantityStepper onChange={onQuantityChange} value={item.quantity} />
        <p className="text-sm font-extrabold text-foreground">
          {formatCatalogMoney(linePrice.subtotal, item.currencyCode)}
        </p>
      </div>
    </article>
  );
}
